import { FC, useMemo, useRef, useState } from 'react';
import type { QualidadePayload, TipoLote } from '@/types/dashboards';
import {
  TODAS_MAQUINAS,
  fichasSemSaldo,
  filaAtual,
  filaPorFaixa,
  kpisQualidade,
  lotesDaTabela,
  lotesDoRecorte,
  prazoPorMaquina,
  prazoPorMes,
  reprocessoDoRecorte,
  reprocessoPorMes,
  resumoMensal,
  type FiltroDesvio,
  type FiltroStatus,
} from '@/utils/equiplexQualidade';
import type { CoresPainel } from '../chartTheme';
import AvisosQualidade from './AvisosQualidade';
import FichasSemSaldoCard from './FichasSemSaldoCard';
import FilaEsperaChart from './FilaEsperaChart';
import FiltrosQualidade from './FiltrosQualidade';
import KpisQualidade from './KpisQualidade';
import LotesTable from './LotesTable';
import PrazoMaquinaChart from './PrazoMaquinaChart';
import PrazoMensalChart from './PrazoMensalChart';
import ResumoQualidadeTable from './ResumoQualidadeTable';

/** Os meses do período do filtro (o servidor já recortou); sem mês passado, o último. */
const mesesDoPeriodo = (payload: QualidadePayload): string[] => {
  const validos = payload.meses_padrao.filter((k) => payload.meses.some((m) => m.key === k));
  return validos.length ? validos : payload.meses.slice(-1).map((m) => m.key);
};

/**
 * A aba com os filtros locais (tipo, máquina, tabela). O período e o produto vêm do filtro
 * dos painéis, aplicado no servidor. Remonta (via `key`) quando chega snapshot novo; com
 * filtro novo o payload muda e os filtros locais ficam.
 */
const PainelQualidade: FC<{ payload: QualidadePayload; cores: CoresPainel }> = ({ payload, cores }) => {
  const meses = useMemo(() => mesesDoPeriodo(payload), [payload]);
  const [tipo, setTipo] = useState<TipoLote>('acabado');
  const [maquina, setMaquina] = useState(TODAS_MAQUINAS);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<FiltroStatus>('todos');
  const [faixa, setFaixa] = useState('todas');
  const [desvio, setDesvio] = useState<FiltroDesvio>('todos');
  const tabelaRef = useRef<HTMLDivElement>(null);

  const filtro = useMemo(() => ({ tipo, meses, maquina }), [tipo, meses, maquina]);
  const recorte = useMemo(() => lotesDoRecorte(payload, filtro), [payload, filtro]);
  const fila = useMemo(() => filaAtual(payload, filtro), [payload, filtro]);
  const kpis = useMemo(() => kpisQualidade(recorte, fila), [recorte, fila]);
  const semSaldo = useMemo(() => fichasSemSaldo(payload, { tipo, maquina }), [payload, tipo, maquina]);
  // Reprocesso é do produto acabado e não tem máquina: some no frasco.
  const reprocesso = tipo === 'acabado' ? reprocessoDoRecorte(payload, meses) : null;
  const linhasTabela = useMemo(
    () => lotesDaTabela(payload, filtro, { busca, status, faixa, desvio }),
    [payload, filtro, busca, status, faixa, desvio],
  );

  const trocarTipo = (novo: TipoLote) => {
    setTipo(novo);
    setMaquina(TODAS_MAQUINAS);
  };

  const irParaTabela = () => tabelaRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  const verFaixa = (novaFaixa: string) => {
    setStatus('todos');
    setFaixa(novaFaixa);
    irParaTabela();
  };

  /** O KPI "passaram da previsão" leva para a tabela já filtrada nos atrasados da fila. */
  const verAtrasados = () => {
    setStatus('todos');
    setFaixa('todas');
    setDesvio('atrasado');
    irParaTabela();
  };

  const inicioJanela = payload.recorte?.inicio ?? payload.meses[0]?.start ?? payload.corte;

  return (
    <>
      <FiltrosQualidade payload={payload} tipo={tipo} onTipo={trocarTipo} maquina={maquina} onMaquina={setMaquina} />
      <AvisosQualidade
        frescor={payload.frescor}
        antigos={kpis.filaForaDaJanela}
        antigosReprocesso={fila.filter((l) => l.fora_da_janela && l.reprocesso).length}
        maisAntigo={fila.reduce<string | null>((min, l) => (l.entrada_cq && (!min || l.entrada_cq < min) ? l.entrada_cq : min), null)}
        inicioJanela={inicioJanela}
        semSaldo={semSaldo.length}
        saldoSemFicha={payload.por_tipo[tipo]?.fila_de_espera.saldo_sem_ficha}
        saldoCq={payload.saldo_deposito_cq}
      />
      <KpisQualidade
        kpis={kpis}
        reprocesso={reprocesso}
        prazoLiberacao={payload.prazo_liberacao?.[tipo] ?? null}
        temPrevisao={!!payload.prazo_liberacao}
        onAtrasados={verAtrasados}
      />

      {/* Um gráfico por linha (26/10/2026). */}
      <FilaEsperaChart barras={filaPorFaixa(payload, fila)} corte={payload.corte} cores={cores} onFaixa={verFaixa} />
      <PrazoMaquinaChart grupos={prazoPorMaquina(recorte)} cores={cores} />

      <PrazoMensalChart meses={prazoPorMes(payload, { tipo, maquina })} cores={cores} />

      <div ref={tabelaRef} className="scroll-mt-6">
        <LotesTable
          lotes={linhasTabela}
          payload={payload}
          busca={busca}
          onBusca={setBusca}
          status={status}
          onStatus={setStatus}
          faixa={faixa}
          onFaixa={setFaixa}
          desvio={desvio}
          onDesvio={setDesvio}
          modo={cores.modo}
        />
      </div>

      <FichasSemSaldoCard fichas={semSaldo} payload={payload} />

      <ResumoQualidadeTable
        linhas={resumoMensal(payload, { tipo, maquina })}
        selecionados={meses}
        reprocesso={tipo === 'acabado' && payload.reprocesso ? reprocessoPorMes(payload) : undefined}
      />

      <details className="rounded-xl border border-theme-border bg-surface-card p-5 text-sm" data-testid="qualidade-notas">
        <summary className="cursor-pointer font-semibold text-content-primary">Como os números são calculados</summary>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-content-secondary">
          {payload.notas.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      </details>
    </>
  );
};

export default PainelQualidade;
